import { useQueryClient } from "@tanstack/react-query";
import { ksUserQK } from "../lib/query-keys";
import { useCurrentUser } from "../lib/hooks/useCurrentUser";
import Title from "../components/ui/Title";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form, FormControl, FormField, FormItem, FormLabel } from "../components/ui/form";
import { Switch } from "../components/ui/switch";
import { User } from "../lib/interfaces/user.interface";
import { useLoaderData } from "react-router";
import Box from "../components/ui/Box";
import { Button } from "../components/ui/button";
import { Check } from "lucide-react";
import { toast } from "sonner";
import { useUpdatePreferencesCommunication } from "../lib/hooks/useUpdatePreferencesCommunication";

const schema = z.object({
  envoyerEmailNouvelleRevue: z.boolean(),
  envoyerEmailWebinaire: z.boolean(),
});
type FieldValues = z.infer<typeof schema>;

export default function PreferencesCommunicationPage() {
  const queryclient = useQueryClient();
  const { user: initialUser } = useLoaderData() as { user: User };
  const { data: user } = useCurrentUser({
    initialData: initialUser,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  const form = useForm<FieldValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      envoyerEmailNouvelleRevue: user?.envoyerEmailNouvelleRevue || false,
      envoyerEmailWebinaire: user?.envoyerEmailWebinaire || false,
    },
  });

  const { mutate: update, isPending } = useUpdatePreferencesCommunication();
  const updatePreferences = (data: FieldValues) => update(data, {
    onSettled: () => {
      queryclient.invalidateQueries({ queryKey: [ksUserQK] });
    },
    onSuccess: () => {
      toast.success("Préférences de communication mises à jour avec succès");
    },
    onError: (err: any) => {
      console.error(err.response?.data || err);
      toast.error(`Une erreur s'est produite`);
    },
  });

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit((data) => updatePreferences(data))}>
        <Box className='mb-6'>
          <Title order={2}>Préférences de communication</Title>

          <div className='mt-6 space-y-6'>
            <FormField
              control={form.control}
              name='envoyerEmailNouvelleRevue'
              render={({ field }) => (
                <FormItem className='flex flex-row items-center gap-4'>
                  <FormControl>
                    <Switch checked={field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                  <div className='space-y-0.5'>
                    <FormLabel className='text-base'>
                      Recevoir un email lors de la publication d'une nouvelle revue numérique
                    </FormLabel>
                    <div className='text-sm text-gray-500 italic'>Pour les abonnés uniquement</div>
                  </div>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name='envoyerEmailWebinaire'
              render={({ field }) => (
                <FormItem className='flex flex-row items-center gap-4'>
                  <FormControl>
                    <Switch checked={field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                  <div className='space-y-0.5'>
                    <FormLabel className='text-base'>
                      Recevoir un email lors de la planification d'un webinaire
                    </FormLabel>
                    <div className='text-sm text-gray-500 italic'>
                      Uniquement applicable si vous avez souscrit un pack d'options
                    </div>
                  </div>
                </FormItem>
              )}
            />
          </div>
        </Box>

        <div className='text-right mb-8 mt-6'>
          <Button icon={<Check />} type='submit' loading={isPending}>
            Sauvegarder
          </Button>
        </div>
      </form>
    </Form>
  );
}
